(() => {
  if (window.BioselfCartDrawer) return;

  const SECTION_ID = 'cart-drawer';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class BioselfCartDrawer {
    constructor() {
      this.lastActiveElement = null;
      this.openStateVersion = 0;
      this.refreshPromise = null;
      this.railQuery = null;
      this.boundRailChange = () => this.updateRail();

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onCartEvent = this.onCartEvent.bind(this);
      this.onAddError = this.onAddError.bind(this);

      document.addEventListener('click', this.onClick);
      document.addEventListener('change', this.onChange);
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('product:added', this.onCartEvent);
      document.addEventListener('product:add-error', this.onAddError);
      document.documentElement.addEventListener('cart:refresh', this.onCartEvent);
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.close({ restoreFocus: false });
        this.refresh();
      });

      this.bindRailQuery();
      this.updateRail();
      this.updateCounters();
    }

    get root() {
      return document.querySelector('[data-bioself-cart]');
    }

    get drawer() {
      return this.root?.querySelector('[data-cart-drawer]');
    }

    get isOpen() {
      return this.root?.classList.contains('is-open') || false;
    }

    onClick(event) {
      const openTrigger = event.target.closest('[data-cart-drawer-open], .cart_link_custom');
      if (openTrigger && this.root) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(openTrigger);
        return;
      }

      if (event.target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const cvpTrigger = event.target.closest('[data-cart-cvp]');
      if (cvpTrigger) {
        event.preventDefault();
        this.openCvpModal(cvpTrigger);
        return;
      }

      const removeTrigger = event.target.closest('[data-cart-remove]');
      if (removeTrigger) {
        const item = removeTrigger.closest('[data-cart-line-item]');
        if (item) this.changeQuantity(item, 0);
        return;
      }

      const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
      if (quantityTrigger) {
        const item = quantityTrigger.closest('[data-cart-line-item]');
        if (!item) return;

        const current = Number.parseInt(item.dataset.cartQuantity || '0', 10);
        const delta = Number.parseInt(quantityTrigger.dataset.cartQuantityChange || '0', 10);
        this.changeQuantity(item, Math.max(0, current + delta));
        return;
      }

    }

    onChange(event) {
      const input = event.target.closest('[data-cart-quantity-input]');
      if (!input) return;

      const item = input.closest('[data-cart-line-item]');
      if (!item) return;

      const quantity = Number.parseInt(input.value, 10);
      if (!Number.isFinite(quantity) || quantity < 1) {
        input.value = item.dataset.cartQuantity || '1';
        return;
      }

      this.changeQuantity(item, quantity);
    }

    onKeyDown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [...(this.drawer?.querySelectorAll(FOCUSABLE_SELECTOR) || [])]
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onCartEvent(event) {
      window.clearTimeout(this.cartEventTimer);
      this.setLoading(true);
      this.cartEventTimer = window.setTimeout(() => {
        this.refresh({ open: this.isOpen }).catch(() => {
          // refresh() already exposes a customer-facing error in the drawer.
        });
      }, 40);
    }

    onAddError(event) {
      const detail = event?.detail || {};
      const message = this.normaliseError(detail.error || detail);

      if (this.isOpen) this.showStatus(message);
    }

    lockPage() {
      if (document.body.classList.contains('bioself-cart-drawer-open')) return;

      document.documentElement.classList.add('bioself-cart-drawer-open');
      document.body.classList.add('bioself-cart-drawer-open');
    }

    unlockPage() {
      document.documentElement.classList.remove('bioself-cart-drawer-open');
      document.body.classList.remove('bioself-cart-drawer-open');
    }

    open(trigger) {
      if (!this.root || !this.drawer) return;

      const wasOpen = this.isOpen;
      this.lastActiveElement = trigger || document.activeElement;
      this.root.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.lockPage();
      this.drawer.focus({ preventScroll: true });
      if (!wasOpen) this.openStateVersion += 1;
    }

    close({ restoreFocus = true } = {}) {
      if (!this.root || !this.drawer) return;

      const wasOpen = this.isOpen;
      this.root.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.unlockPage();
      if (wasOpen) this.openStateVersion += 1;

      if (restoreFocus && this.lastActiveElement?.isConnected) {
        this.lastActiveElement.focus({ preventScroll: true });
      }
    }

    setLoading(isLoading) {
      this.root?.classList.toggle('is-loading', isLoading);
      this.root?.querySelectorAll('[data-cart-loading]').forEach((loader) => {
        loader.setAttribute('aria-hidden', String(!isLoading));
      });
      this.root?.querySelectorAll('button, input').forEach((control) => {
        if (control.matches('[data-cart-drawer-close]')) return;
        control.toggleAttribute('disabled', isLoading);
      });
    }

    showStatus(message = '') {
      const status = this.root?.querySelector('[data-cart-status]');
      if (!status) return;
      status.textContent = message;
    }

    showItemStatus(item, message = '', { animate = false } = {}) {
      if (!item) return false;

      const status = item.querySelector('[data-cart-line-status]');
      const quantityControl = item.querySelector('.bioself-cart-quantity');
      const quantityInput = item.querySelector('[data-cart-quantity-input]');

      if (status) status.textContent = message;

      if (message) quantityInput?.setAttribute('aria-invalid', 'true');
      else quantityInput?.removeAttribute('aria-invalid');

      quantityControl?.classList.remove('is-rejected');

      if (animate && quantityControl) {
        void quantityControl.offsetWidth;
        quantityControl.classList.add('is-rejected');
        window.setTimeout(() => quantityControl.classList.remove('is-rejected'), 650);
      }

      return Boolean(status);
    }

    clearItemStatuses() {
      this.root?.querySelectorAll('[data-cart-line-item]').forEach((item) => {
        this.showItemStatus(item, '');
      });
    }

    getItemContext(item) {
      return item?.closest('[data-cart-rail]') ? 'rail' : 'drawer';
    }

    findItem(key, context = 'drawer') {
      const container = context === 'rail' ? this.root?.querySelector('[data-cart-rail]') : this.drawer;
      return [...(container?.querySelectorAll('[data-cart-line-item]') || [])]
        .find((item) => item.dataset.cartKey === key);
    }

    stockMessage(quantity) {
      return quantity === 1
        ? 'Stock insuficiente. Apenas 1 unidade disponível.'
        : `Stock insuficiente. Apenas ${quantity} unidades disponíveis.`;
    }

    normaliseError(error, fallback = 'Não foi possível adicionar o produto ao carrinho. Tenta novamente.') {
      if (!error) return fallback;
      if (typeof error === 'string') return error;

      if (error.description) return error.description;

      if (error.errors) {
        const errors = Array.isArray(error.errors)
          ? error.errors
          : Object.values(error.errors).flat();
        const message = errors.filter(Boolean).join(' ');
        if (message) return message;
      }

      return error.message || fallback;
    }

    async changeQuantity(item, requestedQuantity) {
      if (this.refreshPromise) return;

      const key = item.dataset.cartKey;
      const context = this.getItemContext(item);
      const max = Number.parseInt(item.dataset.cartMax || '', 10);

      this.showStatus('');
      this.clearItemStatuses();

      if (Number.isFinite(max) && requestedQuantity > max) {
        this.showItemStatus(item, this.stockMessage(max), { animate: true });
        return;
      }

      this.setLoading(true);

      try {
        const response = await fetch(`${window.routes?.cartChangeUrl || '/cart/change'}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: key, quantity: requestedQuantity }),
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw payload;

        const updatedItem = payload.items?.find((cartItem) => cartItem.key === key);
        const appliedQuantity = Number.parseInt(updatedItem?.quantity, 10);
        const quantityWasLimited = requestedQuantity > 0
          && Number.isFinite(appliedQuantity)
          && appliedQuantity < requestedQuantity;

        await this.refresh({ open: this.isOpen });

        if (quantityWasLimited) {
          const refreshedItem = this.findItem(key, context);
          const message = this.stockMessage(appliedQuantity);
          if (!this.showItemStatus(refreshedItem, message, { animate: true })) this.showStatus(message);
        }

        document.documentElement.dispatchEvent(new CustomEvent('bioself:cart-updated', { detail: payload }));
      } catch (error) {
        const message = this.normaliseError(
          error,
          'Não foi possível atualizar a quantidade. Tenta novamente.'
        );
        if (!this.showItemStatus(item, message, { animate: true })) this.showStatus(message);
        this.setLoading(false);
      }
    }

    async refresh({ open = this.isOpen } = {}) {
      if (this.refreshPromise) return this.refreshPromise;

      const currentSection = document.getElementById(`shopify-section-${SECTION_ID}`);
      if (!currentSection) return;

      const initialOpenState = this.isOpen;
      const requestedOpenState = Boolean(open);
      const openStateAtStart = requestedOpenState === initialOpenState
        ? requestedOpenState
        : initialOpenState;
      const openStateVersionAtStart = this.openStateVersion;
      const drawerScrollTop = this.drawer?.querySelector('.bioself-cart-drawer__scrollable')?.scrollTop || 0;
      const railScrollTop = this.root?.querySelector('.bioself-cart-rail__items-wrapper')?.scrollTop || 0;

      const rootUrl = window.routes?.rootUrl || window.Shopify?.routes?.root || '/';
      const separator = rootUrl.includes('?') ? '&' : '?';
      const url = `${rootUrl}${separator}section_id=${SECTION_ID}&_=${Date.now()}`;

      this.refreshPromise = (async () => {
        try {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error(`Erro ${response.status}`);

          const html = await response.text();
          const template = document.createElement('template');
          template.innerHTML = html.trim();
          const nextSection = template.content.querySelector(`#shopify-section-${SECTION_ID}`);
          if (!nextSection) throw new Error('A secção do carrinho não foi devolvida pela Shopify.');

          const finalOpenState = this.openStateVersion !== openStateVersionAtStart
            ? this.isOpen
            : openStateAtStart;

          if (finalOpenState) {
            const nextRoot = nextSection.querySelector('[data-bioself-cart]');
            const nextDrawer = nextRoot?.querySelector('[data-cart-drawer]');
            nextRoot?.classList.add('is-open');
            nextDrawer?.setAttribute('aria-hidden', 'false');
          }

          currentSection.replaceWith(nextSection);
          this.bindRailQuery();
          this.updateRail();
          await this.updateCounters();

          if (finalOpenState) {
            this.lockPage();

            const nextScrollable = this.drawer?.querySelector('.bioself-cart-drawer__scrollable');
            const nextRailWrapper = this.root?.querySelector('.bioself-cart-rail__items-wrapper');
            if (nextScrollable) nextScrollable.scrollTop = drawerScrollTop;
            if (nextRailWrapper) nextRailWrapper.scrollTop = railScrollTop;
          } else {
            this.root?.classList.remove('is-open');
            this.drawer?.setAttribute('aria-hidden', 'true');
            this.unlockPage();
          }
        } catch (error) {
          this.showStatus(this.normaliseError(error));
          throw error;
        } finally {
          this.refreshPromise = null;
          this.setLoading(false);
        }
      })();

      return this.refreshPromise;
    }

    async updateCounters() {
      try {
        const response = await fetch(`${window.routes?.cartUrl || '/cart'}.js`, { credentials: 'same-origin' });
        if (!response.ok) return;
        const cart = await response.json();
        document.querySelectorAll('.header__cart-count').forEach((counter) => {
          counter.textContent = cart.item_count;
          counter.setAttribute('aria-label', `${cart.item_count} artigo(s) no carrinho`);
        });
        if (window.theme) window.theme.cartCount = cart.item_count;
      } catch (_) {
        // The server-rendered count remains a valid fallback.
      }
    }

    bindRailQuery() {
      const root = this.root;
      const minWidth = Number.parseInt(root?.dataset.cartRailMinWidth || '1250', 10);
      const query = `(min-width: ${Number.isFinite(minWidth) ? minWidth : 1250}px)`;

      if (this.railQuery?.media === query) return;
      this.railQuery?.removeEventListener?.('change', this.boundRailChange);
      this.railQuery = window.matchMedia(query);
      this.railQuery.addEventListener?.('change', this.boundRailChange);
    }

    updateRail() {
      const root = this.root;
      const itemCount = Number.parseInt(root?.dataset.cartItemCount || '0', 10);
      const enabled = root?.dataset.cartRailEnabled === 'true';
      const shouldShow = Boolean(root && enabled && itemCount > 0 && this.railQuery?.matches);
      const railWidth = root?.style.getPropertyValue('--bioself-cart-rail-width') || '150px';

      document.documentElement.style.setProperty('--bioself-cart-rail-width', railWidth);
      document.body.classList.toggle('bioself-cart-rail-visible', shouldShow);
    }

    openCvpModal(trigger) {
      if (window.BioselfCvpModal?.open) {
        window.BioselfCvpModal.open(trigger || this.lastActiveElement);
        return;
      }

      const overlay = document.getElementById('modal_overlay');
      const modal = document.getElementById('modal_cruz_vermelha');
      if (!overlay || !modal) return;

      overlay.style.zIndex = '6000';
      overlay.style.display = 'block';
      overlay.setAttribute('aria-hidden', 'false');
      modal.style.zIndex = '6010';
      modal.style.opacity = '1';
      modal.style.visibility = 'visible';
      modal.setAttribute('aria-hidden', 'false');
      modal.querySelector('input, button, [tabindex]')?.focus();
    }
  }

  window.BioselfCartDrawer = new BioselfCartDrawer();
})();
